import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchForms } from "../lib/api-client";
import type { FormSummary } from "../types/api";

function TeamSharingPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const formsQuery = useQuery({
    queryKey: ["forms", teamId],
    queryFn: () => fetchForms({ teamId }),
    enabled: Boolean(teamId),
  });

  const teamForms = useMemo(() => {
    if (!formsQuery.data?.items) return [];
    return formsQuery.data.items.filter((form) => form.teamId === teamId);
  }, [formsQuery.data, teamId]);

  const handleFormClick = (form: FormSummary) => {
    navigate(`/teams/${form.teamId}/forms/${form.id}/sharing`);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>Share Forms</h1>
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            Select a form below to create and manage share links for it.
          </p>
        </div>
      </header>

      <main style={{ padding: "2rem", flex: 1 }}>
        {formsQuery.isLoading ? (
          <div style={{ color: "var(--color-text-muted)" }}>Loading forms...</div>
        ) : teamForms.length === 0 ? (
          <div
            className="surface"
            style={{ padding: "3rem", textAlign: "center", maxWidth: 640, margin: "0 auto" }}
          >
            <h2 style={{ marginBottom: "0.5rem" }}>No forms found</h2>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
              Create a form first to start sharing.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "1.5rem",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            }}
          >
            {teamForms.map((form) => (
              <article
                key={form.id}
                className="surface"
                style={{
                  padding: "1rem",
                  borderRadius: "12px",
                  border: "1px solid var(--color-border)",
                  cursor: "pointer",
                  transition: "border 0.15s ease, transform 0.15s ease",
                }}
                onClick={() => handleFormClick(form)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleFormClick(form);
                  }
                }}
                tabIndex={0}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-primary)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-border)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div className="pill" style={{ marginBottom: "0.5rem" }}>
                  {form.status.toUpperCase()}
                </div>
                <h3 style={{ fontSize: "1.05rem", marginBottom: "0.25rem" }}>
                  {form.title || "Untitled form"}
                </h3>
                <p
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "0.9rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  {form.description ?? "No description"}
                </p>
                <footer
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "var(--color-text-muted)",
                    fontSize: "0.78rem",
                  }}
                >
                  <span>Updated {new Date(form.updatedAt).toLocaleDateString()}</span>
                  <span>v{form.publishedVersionNumber ?? 1}</span>
                </footer>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default TeamSharingPage;
